import { Box } from '@mui/material';
import React, { useEffect, useRef, useMemo, FunctionComponent } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { useSiteSpecificComponents } from '@orgnc/core/hooks';
import RegisteredBlockType from '@orgnc/core/components/blocks/Blocks/types';
import { RenderBlockProps } from '@orgnc/core/components/blocks/Blocks/helpers/RenderBlock';
import {
  StyledEmbedContainer,
  StyledFallbackContainer,
} from './core-embed-style';

// Helper to get video ID from various providers
const getVideoId = (url: string) => {
  if (!url) return null;

  // YouTube URL conversion
  if (url.includes('youtube.com/watch') || url.includes('youtu.be')) {
    const videoId = url.includes('youtu.be')
      ? url.split('/').pop()
      : new URLSearchParams(new URL(url).search).get('v');

    return { id: videoId, provider: 'youtube' };
  }

  // Vimeo URL conversion
  if (url.includes('vimeo.com')) {
    const videoId = url.split('/').pop();
    return { id: videoId, provider: 'vimeo' };
  }

  return null;
};

// Iframe component for videos
const IframeBlock = ({ src }: { src: string }) => {
  return (
    <Box
      component="iframe"
      frameBorder="0"
      height="100%"
      src={src}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
      }}
      width="100%"
      allowFullScreen
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    />
  );
};

// Add typings for window object to handle third-party libraries
declare global {
  interface Window {
    FB: {
      XFBML: {
        parse: (element?: HTMLElement | null) => void;
      };
    };
    instgrm: {
      Embeds: {
        process: () => void;
      };
    };
    twttr: {
      widgets: {
        load: (element?: HTMLElement | null) => void;
      };
    };
    tiktok: {
      embed: {
        reloadEmbedElements: () => void;
      };
    };
  }
}

const CoreEmbed: FunctionComponent<RenderBlockProps> = ({
  area,
  frequency,
  block,
}) => {
  const { providerNameSlug, url, caption, align } = block.attributes;

  const { options } = useSiteSpecificComponents();

  const facebookRef = useRef<HTMLDivElement>(null);
  const twitterRef = useRef<HTMLDivElement>(null);
  const instagramRef = useRef<HTMLDivElement>(null);
  const tiktokRef = useRef<HTMLDivElement>(null);
  const redditRef = useRef<HTMLDivElement>(null);

  // Parse URL info
  const urlInfo = useMemo(() => {
    if (!url) return { isVideo: false, facebookEmbedSrc: '' };

    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname.split('/');
      const isVideo = path.includes('videos');
      const facebookEmbedSrc = isVideo
        ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
            url
          )}&show_text=0`
        : '';

      return { isVideo, facebookEmbedSrc };
    } catch (e) {
      return { isVideo: false, facebookEmbedSrc: '' };
    }
  }, [url]);

  // Helper to sanitize HTML content for embedding
  const createMarkup = (html: string) => {
    return { __html: html };
  };

  // Load social media scripts based on URL
  useEffect(() => {
    if (!url) return undefined;

    const scripts: HTMLScriptElement[] = [];

    // Facebook SDK
    if (
      (url.includes('facebook.com') || url.includes('fb.watch')) &&
      facebookRef.current
    ) {
      if (window.FB) {
        window.FB.XFBML.parse(facebookRef.current);
      } else {
        const script = document.createElement('script');
        script.src =
          'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v17.0';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        document.body.appendChild(script);
        scripts.push(script);
      }
    }

    // Instagram embed
    if (url.includes('instagram.com') && instagramRef.current) {
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      } else {
        const script = document.createElement('script');
        script.src = '//www.instagram.com/embed.js';
        script.async = true;
        document.body.appendChild(script);
        scripts.push(script);
      }
    }

    // Twitter embed
    if (
      (url.includes('twitter.com') || url.includes('x.com')) &&
      twitterRef.current
    ) {
      if (window.twttr) {
        window.twttr.widgets.load(twitterRef.current);
      } else {
        const script = document.createElement('script');
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        document.body.appendChild(script);
        scripts.push(script);
      }
    }

    // TikTok embed
    if (url.includes('tiktok.com') && tiktokRef.current) {
      if (window.tiktok) {
        window.tiktok.embed.reloadEmbedElements();
      } else {
        const script = document.createElement('script');
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        document.body.appendChild(script);
        scripts.push(script);
      }
    }

    // Reddit embed
    if (url.includes('reddit.com') && redditRef.current) {
      const script = document.createElement('script');
      script.src = 'https://embed.reddit.com/widgets.js';
      script.async = true;
      document.body.appendChild(script);
      scripts.push(script);
    }

    // Return cleanup function
    return () => {
      scripts.forEach((script) => {
        try {
          if (script.parentNode) {
            document.body.removeChild(script);
          }
        } catch (e) {
          // Script may have been removed already
        }
      });
    };
  }, [url]);

  // TODO: remove this block in favor of CoreEmbedBlock
  // short-circuit
  if (
    providerNameSlug &&
    options?.CoreEmbedBlock?.providers &&
    options.CoreEmbedBlock.providers[providerNameSlug]
  ) {
    const EmbedComponent = options.CoreEmbedBlock.providers[providerNameSlug];
    return (
      <Box
        className="embed"
        data-og-block-area={area}
        data-og-block-nth={frequency}
        data-og-block-type={RegisteredBlockType.CoreEmbedBlock}
      >
        <EmbedComponent attributes={block.attributes} />
      </Box>
    );
  }

  // Function to create Omny FM embed iframe
  const createOmnyEmbed = () => {
    if (!url) return null;

    // Ensure the URL ends with /embed
    let src = url;
    if (!src.endsWith('/embed')) {
      src = src.endsWith('/') ? `${src}embed` : `${src}/embed`;
    }

    return (
      <Box
        component="iframe"
        src={src}
        title="Omny FM audio"
        width="100%"
        height="180"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        sx={{
          border: 0,
        }}
      />
    );
  };

  // Function to create Facebook embed
  const createFacebookEmbed = () => {
    if (!url) return null;

    if (urlInfo.isVideo) {
      // For Facebook videos, use the responsive iframe approach
      return (
        <Box
          itemProp="associatedMedia"
          sx={{
            paddingBottom: '56.25%',
            position: 'relative',
            height: 0,
            overflow: 'hidden',
          }}
        >
          <IframeBlock src={urlInfo.facebookEmbedSrc} />
        </Box>
      );
    }

    return (
      <div ref={facebookRef}>
        <div
          className={`fb-post`}
          data-href={url}
          data-width="100%"
          data-show-text="true"
        ></div>
      </div>
    );
  };

  // Function to create Instagram embed
  const createInstagramEmbed = () => {
    if (!url) return null;

    return (
      <Box
        ref={instagramRef}
        sx={{
          width: '100%',
          maxWidth: '550px',
          margin: '0 auto',
        }}
      >
        <blockquote
          className="instagram-media"
          data-instgrm-permalink={url}
          data-instgrm-version="14"
          style={{
            background: '#FFF',
            border: 0,
            borderRadius: '3px',
            boxShadow:
              '0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)',
            margin: '1px',
            maxWidth: '540px',
            minWidth: '326px',
            padding: 0,
            width: 'calc(100% - 2px)',
          }}
        >
          <div style={{ padding: '16px' }}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#c9c8cd',
                fontFamily: 'Arial,sans-serif',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 'normal',
                lineHeight: '17px',
                textDecoration: 'none',
                display: 'block',
                textAlign: 'center',
              }}
            >
              View this post on Instagram
            </a>
          </div>
        </blockquote>
      </Box>
    );
  };

  // Function to create Reddit embed
  const createRedditEmbed = () => {
    if (!url) return null;

    return (
      <Box
        ref={redditRef}
        sx={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}
      >
        <blockquote className="reddit-card">
          <a href={url}>{url}</a>
        </blockquote>
      </Box>
    );
  };

  // Function to create SoundCloud embed
  const createSoundCloudEmbed = () => {
    if (!url) return null;

    return (
      <Box
        component="iframe"
        src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(
          url
        )}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}
        title="SoundCloud"
        width="100%"
        height="166"
        scrolling="no"
        frameBorder="no"
        allow="autoplay"
        sx={{ border: 0 }}
      />
    );
  };

  // Function to create X (Twitter) embed
  const createXEmbed = () => {
    if (!url) return null;

    return (
      <div ref={twitterRef}>
        <blockquote className="twitter-tweet">
          <a href={url}>Loading Tweet...</a>
        </blockquote>
      </div>
    );
  };

  // Function to create TikTok embed
  const createTikTokEmbed = () => {
    if (!url) return null;

    return (
      <Box
        ref={tiktokRef}
        sx={{ width: '100%', maxWidth: '325px', margin: '0 auto' }}
      >
        <blockquote
          className="tiktok-embed"
          cite={url}
          data-video-id={url.split('/').pop()}
          style={{ maxWidth: '325px', minWidth: '325px' }}
        >
          <section>
            <a href={url} target="_blank" rel="noreferrer">
              Loading TikTok...
            </a>
          </section>
        </blockquote>
      </Box>
    );
  };

  // Function to create video embed (YouTube, Vimeo)
  const createVideoEmbed = () => {
    if (!url) return null;

    const video = getVideoId(url);
    if (!video?.id) return null;

    const src =
      video.provider === 'vimeo'
        ? `https://player.vimeo.com/video/${video.id}`
        : `https://www.youtube.com/embed/${video.id}?feature=oembed&rel=0&enablejsapi=1`;

    return (
      <Box
        itemProp="associatedMedia"
        sx={{
          paddingBottom: '56.25%',
          position: 'relative',
          height: 0,
          overflow: 'hidden',
        }}
      >
        {video.provider !== 'vimeo' ? (
          <IframeBlock src={src} />
        ) : (
          <LazyLoadComponent threshold={200}>
            <IframeBlock src={src} />
          </LazyLoadComponent>
        )}
      </Box>
    );
  };

  // Render the embed
  const renderEmbed = () => {
    // For Omny FM embeds
    if (url?.includes('omny.fm')) {
      return createOmnyEmbed();
    }

    // For Facebook embeds
    if (url?.includes('facebook.com') || url?.includes('fb.watch')) {
      return createFacebookEmbed();
    }

    // For Instagram embeds
    if (url?.includes('instagram.com')) {
      return createInstagramEmbed();
    }

    // For Reddit embeds
    if (url?.includes('reddit.com')) {
      return createRedditEmbed();
    }

    // For SoundCloud embeds
    if (url?.includes('soundcloud.com')) {
      return createSoundCloudEmbed();
    }

    // For X/Twitter embeds
    if (url?.includes('twitter.com') || url?.includes('x.com')) {
      return createXEmbed();
    }

    // For TikTok embeds
    if (url?.includes('tiktok.com')) {
      return createTikTokEmbed();
    }

    // For video embeds (YouTube, Vimeo, etc.)
    if (
      url?.includes('youtube.com') ||
      url?.includes('youtu.be') ||
      url?.includes('vimeo.com')
    ) {
      return createVideoEmbed();
    }

    if (url) {
      return (
        <StyledFallbackContainer>
          <Box>Unsupported embed URL:</Box>
          <Box>
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          </Box>
        </StyledFallbackContainer>
      );
    }

    return null;
  };

  return (
    <StyledEmbedContainer align={align}>
      {renderEmbed()}
      {caption && (
        <Box
          component="figcaption"
          dangerouslySetInnerHTML={createMarkup(caption)}
        />
      )}
    </StyledEmbedContainer>
  );
};

export default CoreEmbed;
